import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { Send24Regular, Sparkle16Filled } from '@fluentui/react-icons';
import { ChatMessage, sendDiscussMessage } from '../../services/wordDiscuss';
import { AuthUser, getStoredUser } from '../../services/auth';
import { isAccountPlanDisabledError, isFeatureQuotaDepletedError } from '../../services/planLimit';
import { getDocumentText } from '../../word/selection';
import UpgradePrompt from './UpgradePrompt';
import { useUpgradeNavigation } from '../useUpgradeNavigation';

/* global HTMLTextAreaElement, console */

const DOC_CONTEXT_CHAR_CAP = 120_000;

const SUGGESTED_PROMPTS = [
  'What are the most one-sided terms in this document?',
  'Walk me through the termination mechanics.',
  "What would a counterparty's lawyer push back on?",
  'Are there any defined terms used inconsistently?',
];

const useStyles = makeStyles({
  root: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexShrink: 0,
  },
  intro: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    flex: 1,
    minWidth: 0,
  },
  introText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  docHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  messages: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '4px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    padding: '12px 0',
  },
  emptyHint: {
    marginBottom: '4px',
  },
  promptList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'flex-start',
  },
  promptChip: {
    minWidth: 'auto',
    paddingLeft: '10px',
    paddingRight: '10px',
    fontSize: tokens.fontSizeBase200,
    textAlign: 'left',
    height: 'auto',
    minHeight: '28px',
    whiteSpace: 'normal',
  },
  msgRow: {
    display: 'flex',
    width: '100%',
  },
  msgRowUser: {
    justifyContent: 'flex-end',
  },
  msgRowAssistant: {
    justifyContent: 'flex-start',
  },
  bubble: {
    maxWidth: '85%',
    padding: '8px 10px',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase300,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleUser: {
    backgroundColor: '#1014f5',
    color: '#ffffff',
    borderTopRightRadius: '4px',
  },
  bubbleAssistant: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    borderTopLeftRadius: '4px',
  },
  cursor: {
    display: 'inline-block',
    width: '6px',
    height: '1em',
    marginLeft: '2px',
    backgroundColor: tokens.colorNeutralForeground2,
    verticalAlign: 'text-bottom',
    animationName: {
      '0%, 49%': { opacity: 1 },
      '50%, 100%': { opacity: 0 },
    },
    animationDuration: '0.9s',
    animationIterationCount: 'infinite',
  },
  inputRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'flex-end',
    flexShrink: 0,
    paddingTop: '4px',
  },
  textarea: {
    flex: 1,
  },
});

interface DiscussTabProps {
  user: AuthUser | null;
  onAuthChange: (user: AuthUser | null) => void;
  /** The team's whole plan is disabled — parent collapses to the upgrade gate. */
  onPlanLimitHit: () => void;
}

const DiscussTab: React.FC<DiscussTabProps> = ({ user, onAuthChange, onPlanLimitHit }) => {
  const styles = useStyles();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Set only when the chat-messages quota itself runs out — other tabs stay usable.
  const [quotaLimitHit, setQuotaLimitHit] = useState(false);
  const { handleUpgradeClick, upgradeLoading } = useUpgradeNavigation(user, onAuthChange);
  /** Cached document text fetched on tab mount. Passed on session creation. */
  const [docText, setDocText] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(true);
  /** Session ID returned by the backend on first message; reused for the conversation. */
  const sessionIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Pre-load the document into context as soon as the user opens this tab.
  useEffect(() => {
    let cancelled = false;
    setDocLoading(true);
    (async () => {
      try {
        const full = await getDocumentText();
        if (cancelled) return;
        setDocText(full.length > DOC_CONTEXT_CHAR_CAP ? full.slice(0, DOC_CONTEXT_CHAR_CAP) : full);
      } catch (e) {
        if (!cancelled) console.log('Failed to read document text:', e);
      } finally {
        if (!cancelled) setDocLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-scroll to bottom on new content.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, streamingText]);

  const sending = streamingText !== null;

  const send = useCallback(
    async (rawText: string) => {
      const userMessage = rawText.trim();
      if (!userMessage || sending) return;

      const currentUser = getStoredUser();
      if (!currentUser) {
        setError('You must be signed in to Signus to use the discuss feature.');
        return;
      }

      setError(null);
      setQuotaLimitHit(false);
      setInput('');
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      setStreamingText('');

      // Re-read document text on every send so S3 metadata stays current with edits.
      let freshDocText = docText ?? '';
      try {
        const full = await getDocumentText();
        const capped =
          full.length > DOC_CONTEXT_CHAR_CAP ? full.slice(0, DOC_CONTEXT_CHAR_CAP) : full;
        freshDocText = capped;
        setDocText(capped);
      } catch {
        // Use stale docText if re-read fails.
      }

      const controller = new AbortController();
      abortRef.current = controller;

      let acc = '';
      try {
        const result = await sendDiscussMessage({
          sessionId: sessionIdRef.current,
          documentText: freshDocText,
          teamSlug: currentUser.teamSlug,
          accessToken: currentUser.accessToken,
          message: userMessage,
          signal: controller.signal,
          onChunk: chunk => {
            acc += chunk;
            setStreamingText(acc);
          },
        });
        sessionIdRef.current = result.sessionId;
        setMessages(prev => [...prev, { role: 'assistant', content: result.text }]);
      } catch (e) {
        if (acc) setMessages(prev => [...prev, { role: 'assistant', content: acc }]);
        if (isAccountPlanDisabledError(e)) {
          onPlanLimitHit();
        } else if (isFeatureQuotaDepletedError(e)) {
          setQuotaLimitHit(true);
        } else {
          setError(e instanceof Error ? e.message : 'Chat failed.');
        }
      } finally {
        setStreamingText(null);
        abortRef.current = null;
      }
    },
    [sending, docText, onPlanLimitHit], // docText used as initial fallback if re-read fails
  );

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send(input);
    }
  };

  const docHintText = docLoading
    ? 'Loading document…'
    : docText && docText.trim().length > 0
      ? `Document loaded · ${docText.length.toLocaleString()} chars`
      : 'Document is empty';

  return (
    <div className={styles.root}>
      <div className={styles.topBar}>
        <div className={styles.intro}>
          <Sparkle16Filled style={{ color: '#1014f5', flexShrink: 0 }} />
          <span className={styles.introText}>Chat with Sigi about this document.</span>
        </div>
        <span className={styles.docHint}>{docHintText}</span>
      </div>

      {quotaLimitHit ? (
        <UpgradePrompt onUpgrade={handleUpgradeClick} loading={upgradeLoading} />
      ) : (
        error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )
      )}

      <div ref={scrollerRef} className={styles.messages}>
        {messages.length === 0 && streamingText === null && (
          <div className={styles.emptyState}>
            <div className={styles.emptyHint}>Try one of these to get started:</div>
            <div className={styles.promptList}>
              {SUGGESTED_PROMPTS.map(p => (
                <Button
                  key={p}
                  className={styles.promptChip}
                  size="small"
                  appearance="secondary"
                  onClick={() => send(p)}
                  disabled={sending}
                >
                  {p}
                </Button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`${styles.msgRow} ${m.role === 'user' ? styles.msgRowUser : styles.msgRowAssistant}`}
          >
            <div
              className={`${styles.bubble} ${m.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant}`}
            >
              {m.content}
            </div>
          </div>
        ))}

        {streamingText !== null && (
          <div className={`${styles.msgRow} ${styles.msgRowAssistant}`}>
            <div className={`${styles.bubble} ${styles.bubbleAssistant}`}>
              {streamingText}
              <span className={styles.cursor} />
            </div>
          </div>
        )}
      </div>

      <div className={styles.inputRow}>
        <Textarea
          className={styles.textarea}
          value={input}
          placeholder="Ask Sigi… (Cmd/Ctrl+Enter to send)"
          rows={2}
          disabled={sending}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {sending ? (
          <Button appearance="secondary" onClick={handleStop}>
            Stop
          </Button>
        ) : (
          <Button
            appearance="primary"
            icon={<Send24Regular />}
            disabled={!input.trim()}
            onClick={() => send(input)}
          />
        )}
      </div>
    </div>
  );
};

export default DiscussTab;
